<template>
  <!-- Global notification live region, render this permanently at the end of the document -->
  <div aria-live="assertive" class="pointer-events-none fixed inset-0 flex items-start px-4 py-6 sm:items-start sm:p-6">
    <div class="flex w-full flex-col items-end space-y-4">
      <!-- Notification panel, dynamically insert this into the live region when it needs to be displayed -->
      <transition
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="show"
          class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5"
        >
          <div class="p-4">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <CheckCircleIcon class="h-6 w-6 text-green-400" aria-hidden="true" v-if="success" />
                <XCircleIcon class="h-6 w-6 text-red-400" aria-hidden="true" v-else />
              </div>
              <div class="ml-3 w-0 flex-1 pt-0.5">
                <p class="text-sm font-medium m-0" :class="success ? 'text-gray-900' : 'text-red-900'">
                  {{ title }}
                </p>
                <p class="mt-1 text-sm text-gray-500 mb-0">{{ description }}</p>
                <div v-if="htmlMessage" v-html="htmlMessage" ref="htmlContainer"></div>
              </div>
              <div class="ml-4 flex flex-shrink-0 notification">
                <button
                  type="button"
                  @click="show = false"
                  class="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-5 w-5 fill-gray-400" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, getCurrentInstance, watch, nextTick } from 'vue';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline';
import { XMarkIcon } from '@heroicons/vue/20/solid';
import { useRouter } from 'vue-router';
// Access global properties
const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
const router = useRouter();
const title = ref('');
const description = ref('');
const show = ref(false);
const success = ref(true);
const htmlMessage = ref('');
const htmlContainer = ref(null);
let clickHandler;

onMounted(() => {
  // Assuming you have a global event emitter system set up, listening for 'notification' events
  $emitter.on('notification', (data) => {
    title.value = data.title || '';
    description.value = data.description || '';
    success.value = data.success === false ? false : true;
    htmlMessage.value = '';
    nextTick(() => {
      htmlMessage.value = data.htmlMessage || '';
    });
    // Update visibility of the notification
    show.value = true;
    setTimeout(() => {
      show.value = false;
      removeClickListener();
    }, 5000);
  });
});

watch(htmlMessage, async (newVal) => {
  removeClickListener();
  if (newVal) {
    await nextTick();
    clickHandler = (e) => {
      const route = e.target?.dataset?.route;
      if (route) {
        e.preventDefault();
        router.push(route);
      }
    };
    htmlContainer.value?.addEventListener('click', clickHandler);
  }
});

const removeClickListener = () => {
  if (clickHandler && htmlContainer.value) {
    htmlContainer.value.removeEventListener('click', clickHandler);
    clickHandler = null;
  }
};

// Cleanup listener when the component is unmounted
onUnmounted(() => {
  $emitter.off('notification');
  removeClickListener();
});
</script>
